import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { DialogWithForm } from '@strata/react-dialog';
import { FormSectionRow } from '@strata/react-form-section';
import { InlineMessage } from '@strata/react-inline-message';
import { DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { SuccessToast } from '@strata/react-toast-message';
import { type WorklistTask, WorklistTimer } from '@strata/react-worklist';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { usePermissionCheck } from 'services/APIHooks';
import { Permissions } from 'services/constants';
import { handleError } from 'services/Error';
import { useAppDispatch, useAppSelector } from 'store';
import { usePutLddCapabilitiesMutation } from 'store/api/referenceApi';
import { usePostConfirmLddAccessMutation } from 'store/api/worklistApi';
import { fetchWorklistTasks } from 'store/worklist/worklistThunks';

const AccessOptions = {
    BRAND: 'BRAND',
    GENERIC: 'GENERIC',
} as const;

interface LddWorklistTask extends WorklistTask {
    task_info?: WorklistTask['task_info'] & { generic_available?: boolean };
}

const DrugVersion = {
    GENERIC: 2,
    EITHER: 3,
} as const;

const isGenericAvailable = (task?: LddWorklistTask) => Boolean(task?.task_info?.generic_available);

const formatSubmitValues = ({ oid, access }: { oid: string; access: string[] }) => ({
    added_capabilities: [
        {
            product_service_oid: oid,
            drug_version: access.includes(AccessOptions.BRAND)
                ? DrugVersion.EITHER
                : DrugVersion.GENERIC,
        },
    ],
    removed_capabilities: [],
});

interface ConfirmLddAccessDialogProps {
    isOpen: boolean;
    worklistTask?: LddWorklistTask;
    onClose: () => void;
}

export const ConfirmLddAccessDialog = ({
    isOpen,
    worklistTask = undefined,
    onClose,
}: ConfirmLddAccessDialogProps) => {
    const dispatch = useAppDispatch();
    const selectedNpi = useAppSelector((state) => state.auth.selectedNpi);
    const writeWorklist = usePermissionCheck(Permissions.WRITE_WORKLIST);
    const [putLddCapabilities] = usePutLddCapabilitiesMutation();
    const [postConfirmLddAccess] = usePostConfirmLddAccessMutation();
    const genericAvailable = isGenericAvailable(worklistTask);

    return (
        <Formik
            initialValues={{ access: genericAvailable ? [] : [AccessOptions.BRAND] }}
            enableReinitialize
            validationSchema={Yup.object({
                access: Yup.array(Yup.string().required())
                    .required(validationStrings.requiredField.errorMessage)
                    .min(1, validationStrings.requiredField.errorMessage),
            })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    const oid = worklistTask?.task_info?.product_service_oid;
                    if (!worklistTask || !oid || !selectedNpi) {
                        handleError('An error occurred confirming LDD access.');
                        return;
                    }
                    await putLddCapabilities({
                        pharmacyNpi: selectedNpi,
                        ...formatSubmitValues({
                            oid,
                            access: values.access,
                        }),
                    }).unwrap();
                    await postConfirmLddAccess({ taskId: worklistTask.task_id }).unwrap();
                    await dispatch(fetchWorklistTasks());
                    SuccessToast({ message: 'LDD access confirmed successfully.' });
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('An error occurred confirming LDD access.', err as Error);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={worklistTask?.task_type_display_name ?? 'Confirm LDD Access'}
                    isOpen={isOpen}
                    width={616}
                    primaryButtonLabel="Confirm Access"
                    isPrimaryButtonDisabled={
                        !writeWorklist ||
                        (genericAvailable && !(formik.isValid && formik.dirty)) ||
                        formik.isSubmitting
                    }
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        formik.resetForm();
                        onClose();
                    }}
                    dialogContent={
                        <>
                            <div className="margin__bottom--large">
                                <InlineMessage
                                    intent={InlineMessage.Intent.WARNING}
                                    wrapText
                                    label="By confirming access to this drug, you certify that your pharmacy is able to dispense and service it for referred patients. Inaccurate confirmations may result in referrals being assigned to your pharmacy for drugs you are unable to support."
                                />
                            </div>
                            {!genericAvailable && (
                                <div className="margin__bottom--large">
                                    <InlineMessage
                                        intent={InlineMessage.Intent.INFO}
                                        wrapText
                                        label="A generic version of this LDD is not currently available but if it is introduced in the future, your pharmacy will automatically become eligible for the generic."
                                    />
                                </div>
                            )}
                            {worklistTask && (
                                <FormSectionRow
                                    rowContent={
                                        <>
                                            <WorklistTimer
                                                dueDate={worklistTask.due_date}
                                                warningDate={worklistTask.warning_date}
                                                completedDate={worklistTask.completed_date}
                                            />
                                            <DetailLabelValuePair
                                                label="Product"
                                                value={worklistTask.task_info?.product_name}
                                            />
                                        </>
                                    }
                                />
                            )}
                            {genericAvailable && (
                                <>
                                    <div className="margin__bottom--large">
                                        Select the drug type(s) your pharmacy can dispense and
                                        service.
                                    </div>
                                    <CheckboxListFormik
                                        name="access"
                                        required
                                        disabled={!writeWorklist}
                                        options={[
                                            { value: AccessOptions.BRAND, text: 'Brand' },
                                            { value: AccessOptions.GENERIC, text: 'Generic' },
                                        ]}
                                    />
                                </>
                            )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};

ConfirmLddAccessDialog.formatSubmitValues = formatSubmitValues;
